import { type AssetClass, CANONICAL_ASSET_CLASSES } from '@vizit/api';
import {
  Button,
  ButtonLevel,
  ButtonSize,
  Menu,
  MenuItem,
  MenuPopover,
  MenuTrigger,
  Text,
  TextColor,
  TextSize,
} from '@vizit/atoms';
import { Edit02Icon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { useState } from 'react';
import { FocusScope } from 'react-aria';

/**
 * Props for AssetClassDropdown.
 */
interface AssetClassDropdownProps {
  /**
   * The currently-applied classification. Drives the menu's selected-item check.
   * @required
   * @type {AssetClass | null | undefined}
   */
  value: AssetClass | null | undefined;
  /**
   * Callback fired when the user picks a new class. Not fired when re-picking the current value.
   * @required
   * @type {(next: AssetClass) => void}
   */
  onChange: (next: AssetClass) => void;
  /**
   * Disables the trigger (e.g. while a mutation is in flight).
   * @optional
   * @type {boolean}
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Accessible label for the trigger button.
   * @optional
   * @type {string}
   * @default 'Change asset class'
   */
  ariaLabel?: string;
  /**
   * Pass-through for the trigger button's class.
   * @optional
   * @type {string}
   */
  className?: string;
  /**
   * The original (ML-detected) classification. When set, the dropdown footer
   * always shows "Originally classified as {originalClassification}".
   * null/undefined → no footer.
   * @optional
   * @type {AssetClass | null | undefined}
   * @default undefined (no footer shown)
   */
  originalClassification?: AssetClass | null;
  /**
   * Called when the user clicks "Reset". The Reset button appears only when an
   * `originalClassification` is set AND the current `value` differs from it
   * (i.e. the class has been overridden).
   * @optional
   * @type {() => void}
   * @default undefined (Reset button hidden)
   */
  onReset?: () => void;
}

/**
 * Dropdown for changing an asset's classification.
 * Pencil-icon trigger opens a menu of the 14 selectable classes, alphabetical.
 *
 * This dropdown only renders for scraped carousel images (gated by
 * `is_class_editable`). The `AssetClass` union has 15 members — the 14 in
 * `CANONICAL_ASSET_CLASSES` plus `Hero Image`, which that list deliberately
 * omits (see its definition in @vizit/api) because hero-vs-carousel is a slot
 * decision, not a classification: the BE rejects the case (SPM-2904), so we
 * skip offering it rather than make the user recover from an error toast.
 *
 * @param props - The component props
 * @param props.value - The currently-applied classification
 * @param props.onChange - Callback fired when the user picks a new class
 * @param props.isDisabled - Disables the trigger
 * @param props.ariaLabel - Accessible label for the trigger button
 * @param props.className - Pass-through for the trigger button's class
 * @param props.originalClassification - Original ML-detected classification shown in the reset footer
 * @param props.onReset - Called when the user clicks "Reset" in the footer
 */
function AssetClassDropdown(props: AssetClassDropdownProps) {
  const {
    value,
    onChange,
    isDisabled,
    ariaLabel = 'Change asset class',
    className,
    originalClassification,
    onReset,
  } = props;

  // Reset is offered only once the class has been overridden — i.e. the current
  // value differs from the original (ML) classification.
  const showReset = Boolean(onReset) && value !== originalClassification;

  // Controlled so pressing Reset can close the popover before the confirmation
  // dialog opens — otherwise the popover's focus trap (`contain`, below) would
  // fight the dialog for focus, and the dropdown would sit under the dialog.
  const [isOpen, setIsOpen] = useState(false);

  return (
    <MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button
        aria-label={ariaLabel}
        isDisabled={isDisabled}
        className={className}
        level={ButtonLevel['Text-Secondary']}
        size={ButtonSize.Sm}
      >
        <Edit02Icon size={16} />
      </Button>
      <MenuPopover placement="bottom start">
        {/* `contain` traps Tab within the popover so focus can reach the Reset
            button (which lives outside <Menu> and so isn't part of the menu's
            arrow-key navigation) and the menu doesn't dismiss when focus leaves
            the item list — making Reset reachable for keyboard / screen-reader
            users while keeping the inline footer design. */}
        <FocusScope contain>
          <Menu
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={value ? new Set([value]) : new Set()}
            onSelectionChange={(keys) => {
              if (keys === 'all') return;
              const next = keys.values().next().value as AssetClass | undefined;
              if (next && next !== value) {
                onChange(next);
              }
            }}
          >
            {CANONICAL_ASSET_CLASSES.map((c) => (
              <MenuItem key={c} id={c} textValue={c}>
                {c}
              </MenuItem>
            ))}
          </Menu>
          {/* pl-8 aligns the message with the menu items' text, which sit at
              mx-3 (12px) + px-5 (20px) = 32px from the popover edge. When Reset
              isn't shown, the right padding matches the left (pr-8) so the lone
              message is evenly inset; with Reset present, pr-3 hugs the button. */}
          {originalClassification && (
            <div
              className={cx(
                'flex items-center justify-between gap-4 border-t border-primary py-2 pl-8',
                showReset ? 'pr-3' : 'pr-8',
              )}
            >
              <Text size={TextSize.Md} color={TextColor.Tertiary} showEllipsis={false}>
                Originally classified as {originalClassification}
              </Text>
              {showReset && (
                <Button
                  level={ButtonLevel['Text-Secondary']}
                  size={ButtonSize.Sm}
                  onPress={() => {
                    setIsOpen(false);
                    onReset?.();
                  }}
                >
                  Reset
                </Button>
              )}
            </div>
          )}
        </FocusScope>
      </MenuPopover>
    </MenuTrigger>
  );
}

export { AssetClassDropdown };
export type { AssetClassDropdownProps };
